import React, { useState, useEffect } from 'react';
import { AlertTriangle, TrendingDown, ShieldAlert, X, ChevronRight, Activity, ArrowUpRight } from 'lucide-react';
import { MacroEventSignal } from '../types';
import { fetchMacroEventSignals } from '../services/macroEventService';

interface MacroShockBannerProps {
  onOpenEventImpact?: (event: MacroEventSignal) => void;
  lang?: 'ko' | 'en';
}

export const MacroShockBanner: React.FC<MacroShockBannerProps> = ({ onOpenEventImpact, lang = 'ko' }) => {
  const [shockEvent, setShockEvent] = useState<MacroEventSignal | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadShock() {
      try {
        const res = await fetchMacroEventSignals({ limit: 1 });
        if (!isMounted) return;

        if (res.latest && (res.latest.shock_level === 'CRITICAL' || res.latest.shock_level === 'WARNING')) {
          // 24시간 자동 만료(Auto-Expiration) 검증
          const releaseDateTimeStr = `${res.latest.release_date}T${res.latest.release_time || '08:30:00'}`;
          const releaseTimeMs = new Date(releaseDateTimeStr).getTime();
          const isExpired = !isNaN(releaseTimeMs) && (Date.now() - releaseTimeMs > 24 * 60 * 60 * 1000);

          if (!isExpired) {
            // Check if previously dismissed in localStorage
            const dismissedId = localStorage.getItem('ezoptix_dismissed_shock');
            if (dismissedId !== res.latest.id) {
              setShockEvent(res.latest);
            }
          }
        }
      } catch (err) {
        console.error('[MacroShockBanner] Load error:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadShock();
    return () => { isMounted = false; };
  }, []);

  if (loading || !shockEvent || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    if (shockEvent) {
      localStorage.setItem('ezoptix_dismissed_shock', shockEvent.id);
    }
    setIsDismissed(true);
  };

  const isCritical = shockEvent.shock_level === 'CRITICAL';
  const surpriseSign = (shockEvent.surprise_delta ?? 0) > 0 ? '+' : '';

  const formatUnit = (type: string, val: number | null | undefined) => {
    if (val === null || val === undefined) return '-';
    const t = (type || '').toUpperCase();
    if (t.includes('NFP') || t.includes('PAYROLL')) return `${val > 0 ? '+' : ''}${val}k`;
    if (t.includes('PMI') || t.includes('ISM')) return `${val}p`;
    return `${val}%`;
  };

  const getRemainingHours = () => {
    const releaseDateTimeStr = `${shockEvent.release_date}T${shockEvent.release_time || '08:30:00'}`;
    const releaseTimeMs = new Date(releaseDateTimeStr).getTime();
    if (isNaN(releaseTimeMs)) return null;
    const remainingHours = Math.max(0, Math.round(24 - (Date.now() - releaseTimeMs) / (1000 * 60 * 60)));
    return remainingHours;
  };

  const remainingHours = getRemainingHours();

  return (
    <div className={`relative mb-5 overflow-hidden rounded-xl border p-4 transition-all duration-300 shadow-lg ${
      isCritical
        ? 'bg-gradient-to-r from-red-950/60 via-zinc-900/90 to-red-950/40 border-red-500/50 shadow-red-950/30'
        : 'bg-gradient-to-r from-amber-950/60 via-zinc-900/90 to-amber-950/40 border-amber-500/50 shadow-amber-950/30'
    }`}>
      {/* Background Pulse Glow */}
      <div className={`absolute -right-12 -top-12 h-36 w-36 rounded-full blur-3xl opacity-20 pointer-events-none ${
        isCritical ? 'bg-red-500' : 'bg-amber-500'
      }`} />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Left Section: Badges & Details */}
        <div className="flex items-start gap-3.5 flex-1">
          <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${
            isCritical
              ? 'bg-red-500/20 text-red-400 border-red-500/40'
              : 'bg-amber-500/20 text-amber-400 border-amber-500/40'
          }`}>
            {isCritical ? <ShieldAlert className="h-5 w-5 animate-pulse" /> : <AlertTriangle className="h-5 w-5" />}
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                isCritical
                  ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
              }`}>
                <span className="h-1.5 w-1.5 rounded-full bg-current animate-ping" />
                {lang === 'ko' ? '긴급 매크로 쇼크' : 'Macro Shock Alert'}
              </span>

              <span className="text-xs font-semibold text-zinc-300">
                {shockEvent.event_type} {lang === 'ko' ? '발표치' : 'Release'} {formatUnit(shockEvent.event_type, shockEvent.actual_value)}
                <span className="text-zinc-400 ml-1">
                  ({lang === 'ko' ? '예상' : 'Est'} {formatUnit(shockEvent.event_type, shockEvent.consensus_value)} 대비 {surpriseSign}{shockEvent.surprise_delta}{shockEvent.event_type.includes('NFP') ? 'k' : shockEvent.event_type.includes('PMI') ? 'p' : '%p'})
                </span>
              </span>

              <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                shockEvent.portfolio_guidance === 'HEDGE_REQUIRED'
                  ? 'bg-red-900/50 text-red-200 border-red-700/60'
                  : 'bg-amber-900/50 text-amber-200 border-amber-700/60'
              }`}>
                {lang === 'ko' ? '포트폴리오 권고:' : 'Action:'} {shockEvent.portfolio_guidance}
              </span>

              {remainingHours !== null && remainingHours > 0 && (
                <span className="text-[10px] text-zinc-400/80 bg-zinc-800/60 px-1.5 py-0.5 rounded border border-white/5 font-mono">
                  ⏱ {lang === 'ko' ? `${remainingHours}시간 후 자동 만료` : `${remainingHours}h remaining`}
                </span>
              )}
            </div>

            <p className="text-xs text-zinc-300/90 leading-relaxed max-w-4xl">
              {shockEvent.summary}
            </p>

            {/* Market Reaction Tickers */}
            {shockEvent.market_reaction && (
              <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] text-zinc-400">
                {shockEvent.market_reaction.nasdaq_futures !== undefined && (
                  <span className="flex items-center gap-1 font-mono">
                    <TrendingDown className="h-3 w-3 text-red-400" />
                    나스닥 선물: <span className="text-red-400 font-semibold">{shockEvent.market_reaction.nasdaq_futures}%</span>
                  </span>
                )}
                {shockEvent.market_reaction.vix !== undefined && (
                  <span className="flex items-center gap-1 font-mono">
                    <Activity className="h-3 w-3 text-amber-400" />
                    VIX: <span className="text-amber-300 font-semibold">{shockEvent.market_reaction.vix}</span>
                  </span>
                )}
                {shockEvent.market_reaction.us10y !== undefined && (
                  <span className="flex items-center gap-1 font-mono">
                    <ArrowUpRight className="h-3 w-3 text-red-400" />
                    미국채 10년물: <span className="text-zinc-200 font-semibold">{shockEvent.market_reaction.us10y}%</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section: Actions */}
        <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
          {onOpenEventImpact && (
            <button
              onClick={() => onOpenEventImpact(shockEvent)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shadow ${
                isCritical
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-amber-600 hover:bg-amber-500 text-white'
              }`}
            >
              {lang === 'ko' ? '포트폴리오 영향도 시뮬레이션' : 'Simulate Portfolio Impact'}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}

          <button
            onClick={handleDismiss}
            className="p-1 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors"
            title={lang === 'ko' ? '닫기' : 'Dismiss'}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
